import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Moon, DollarSign, AlertTriangle, CheckCircle, BedDouble, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function NightAudit() {
  const [audits, setAudits] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashInHand, setCashInHand] = useState('');
  const [notes, setNotes] = useState('');
  const [running, setRunning] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [auditsRes, latestRes] = await Promise.all([
        api.get('/night-audit?limit=10'),
        api.get('/night-audit/latest'),
      ]);
      setAudits(auditsRes.data);
      setLatest(latestRes.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runAudit = async () => {
    if (!cashInHand) { toast.error('Enter cash in hand'); return; }
    setRunning(true);
    try {
      const res = await api.post('/night-audit', { cash_in_hand: parseFloat(cashInHand), notes });
      toast.success('Night audit completed');
      setLatest(res.data);
      setCashInHand('');
      setNotes('');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setRunning(false); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  const today = new Date().toISOString().slice(0, 10);
  const todayAuditDone = latest?.date === today;

  return (
    <div data-testid="night-audit-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Night Audit</h1>
        <p className="text-stone-500 mt-1">Daily reconciliation and cash closing</p>
      </div>

      {/* Run Audit */}
      {!todayAuditDone ? (
        <div className="bg-white border-2 border-sage-200 rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Moon className="w-5 h-5 text-sage-700" />
            <h2 className="font-semibold text-stone-900 font-['Manrope']">Run Tonight's Audit</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Cash in Hand (₹)</Label>
              <Input type="number" min="0" step="0.01" value={cashInHand} onChange={e => setCashInHand(e.target.value)} placeholder="0.00" className="border-stone-300" data-testid="cash-in-hand" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any observations..." className="border-stone-300 min-h-[40px]" />
            </div>
            <div className="flex items-end">
              <Button onClick={runAudit} disabled={running} className="bg-sage-700 hover:bg-sage-800 text-white w-full" data-testid="run-audit-btn">
                {running ? 'Processing...' : 'Run Night Audit'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-6 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-700" />
          <div>
            <p className="font-medium text-emerald-800">Today's audit completed</p>
            <p className="text-sm text-emerald-600">by {latest.audited_by_name} at {latest.created_at?.slice(11, 16)} UTC</p>
          </div>
        </div>
      )}

      {/* Latest Audit Summary */}
      {latest && latest.revenue && (
        <div className="bg-white border border-stone-200 rounded-md p-6 mb-6">
          <h3 className="font-semibold text-stone-900 font-['Manrope'] mb-4">Latest Audit: {latest.date}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-stone-50 rounded-md">
              <p className="text-xs text-stone-500">Total Revenue</p>
              <p className="text-lg font-bold text-stone-900">{fmt(latest.revenue.total)}</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-md">
              <p className="text-xs text-stone-500">Room Revenue</p>
              <p className="text-lg font-bold text-stone-900">{fmt(latest.revenue.room_revenue)}</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-md">
              <p className="text-xs text-stone-500">F&B Revenue</p>
              <p className="text-lg font-bold text-stone-900">{fmt(latest.revenue.fnb_revenue)}</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-md">
              <p className="text-xs text-stone-500">Occupancy</p>
              <p className="text-lg font-bold text-stone-900">{latest.operations.occupancy_rate}%</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-blue-50 rounded-md text-center">
              <p className="text-xs text-blue-600">Cash</p><p className="font-bold text-blue-800">{fmt(latest.revenue.cash)}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-md text-center">
              <p className="text-xs text-purple-600">Card</p><p className="font-bold text-purple-800">{fmt(latest.revenue.card)}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-md text-center">
              <p className="text-xs text-emerald-600">Transfer</p><p className="font-bold text-emerald-800">{fmt(latest.revenue.bank_transfer)}</p>
            </div>
          </div>
          {/* Cash Reconciliation */}
          {latest.cash_reconciliation && (
            <div className={`p-3 rounded-md ${Math.abs(latest.cash_reconciliation.variance) > 1 ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Cash Reconciliation</span>
                <span className={`text-sm font-bold ${Math.abs(latest.cash_reconciliation.variance) > 1 ? 'text-red-700' : 'text-emerald-700'}`}>
                  Variance: {fmt(latest.cash_reconciliation.variance)}
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1">Expected: {fmt(latest.cash_reconciliation.expected_cash)} | Actual: {fmt(latest.cash_reconciliation.actual_cash)}</p>
            </div>
          )}
          {/* Discrepancies */}
          {latest.discrepancies?.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Discrepancies</p>
              {latest.discrepancies.map((d, i) => <p key={i} className="text-sm text-amber-700 mt-1">- {d}</p>)}
            </div>
          )}
        </div>
      )}

      {/* Audit History */}
      <h3 className="font-semibold text-stone-900 font-['Manrope'] mb-3">Audit History</h3>
      <div className="space-y-2">
        {audits.map(a => (
          <div key={a.id} className="bg-white border border-stone-200 rounded-md p-3 flex items-center justify-between" data-testid={`audit-${a.date}`}>
            <div>
              <p className="font-medium text-stone-900">{a.date}</p>
              <p className="text-sm text-stone-500">by {a.audited_by_name}</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-stone-900">{fmt(a.revenue?.total)}</p>
              <p className="text-sm text-stone-500">{a.operations?.occupancy_rate}% occ</p>
            </div>
            {a.discrepancies?.length > 0 && <AlertTriangle className="w-4 h-4 text-amber-500" />}
          </div>
        ))}
        {audits.length === 0 && <p className="text-stone-500 text-center py-8">No audits yet. Run your first night audit above.</p>}
      </div>
    </div>
  );
}
